import { Controller } from "react-hook-form";

// eslint-disable-next-line deprecate/import
import { m7DayJs, Timezone } from "@m7-health/shared-utils";
import { get } from "lodash";
import upperFirst from "lodash/upperFirst";

import { FormControl, FormHelperText } from "@mui/material";
import { LocalizationProvider, PickersActionBarAction } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { Dayjs } from "~/common/packages/dayjs";
import { redAlert } from "~/common/theming/colors";

import { UpdatedAdapterDayjs } from "@/common/packages/dayjs/date-picker-adapter";

import { ICustomDatePickerControlledProps, ICustomDatePickerProps } from "./types";

const onChangeWrapper = (
  date: Dayjs | undefined | null,
  onChange: (date: Dayjs | null | undefined) => void,
  timezone?: Timezone,
  clearable?: boolean,
) => {
  if (clearable && !date) return onChange(null);
  if (!date?.isValid()) return;

  // When manual input from null value, the localizer don't properly set timezone
  //  so we check and set it if needed
  if (timezone && !get(date, "$x.$timezone")) date = date?.tz(timezone);
  onChange(date?.startOf("day") || null);
};

/** @deprecated Use Luxon based `CustomDatePickerControlled` instead */
export const CustomDatePickerControlled = ({
  control,
  disabled,
  name,
  ...rest
}: ICustomDatePickerControlledProps) => {
  return (
    <Controller
      name={name || ""}
      control={control}
      render={({ field: { onChange, value } }) => (
        <CustomDatePicker
          {...rest}
          disabled={disabled}
          onChange={(date) => onChangeWrapper(date, onChange, rest.timezone, rest.clearable)}
          value={value as undefined | Dayjs}
          name={name}
        />
      )}
    />
  );
};

/** @deprecated Use Luxon based `CustomDatePicker` instead */
export const CustomDatePicker = ({
  disabled,
  onChange,
  value,
  errors,
  name,
  label,
  slotProps,
  clearable,
  ...rest
}: ICustomDatePickerProps) => {
  const errorMessage = name ? upperFirst((errors?.[name]?.message as string) ?? "") : null;

  return (
    <FormControl sx={{ width: "100%" }}>
      <LocalizationProvider dateAdapter={UpdatedAdapterDayjs} dateLibInstance={m7DayJs}>
        <DatePicker
          disabled={disabled}
          value={value}
          onChange={(date) => onChangeWrapper(date, onChange, rest.timezone, clearable)}
          label={label}
          slotProps={{
            ...slotProps,
            actionBar: {
              actions: [
                ...(clearable ? ["clear"] : []),
                ...(get(slotProps?.actionBar, "actions") || []),
              ] as PickersActionBarAction[],
              ...(slotProps?.actionBar || {}),
            },
            textField: {
              error: !!errorMessage,
              size: "small",
              ...(slotProps?.textField || {}),
            },
          }}
          {...rest}
        />
      </LocalizationProvider>
      {errorMessage && <FormHelperText sx={{ color: redAlert }}>{errorMessage}</FormHelperText>}
    </FormControl>
  );
};
